/**
 * Device Model Detection Utilities
 * Intelligent detection of specific Apple device models based on device name and model identifier
 */

export const detectIphoneModel = (
  deviceName?: string,
  modelIdentifier?: string
): string => {
  if (!deviceName && !modelIdentifier) return 'standard';
  
  const name = (deviceName || '').toLowerCase();
  const identifier = (modelIdentifier || '').toLowerCase();
  
  // iPhone 15 Pro Max - handle both space and hyphen
  if (name.includes('iphone 15 pro max') || name.includes('iphone-15-pro-max') || identifier.includes('iphone16,2')) {
    return 'iphone-15-pro-max';
  }
  
  // iPhone 15 Pro - handle both space and hyphen
  if (name.includes('iphone 15 pro') || name.includes('iphone-15-pro') || identifier.includes('iphone16,1')) {
    return 'iphone-15-pro';
  }
  
  // iPhone 15 / 15 Plus - handle both space and hyphen
  if (name.includes('iphone 15') || name.includes('iphone-15') || identifier.includes('iphone15,')) {
    return 'iphone-15-pro'; // Use Pro as fallback for 15 series
  }
  
  // iPhone 14 Pro - handle both space and hyphen
  if (name.includes('iphone 14 pro') || name.includes('iphone-14-pro') || identifier.includes('iphone15,')) {
    return 'iphone-14-pro';
  }
  
  // iPhone 14 - handle both space and hyphen
  if (name.includes('iphone 14') || name.includes('iphone-14') || identifier.includes('iphone14,')) {
    return 'iphone-14-pro'; // Use Pro variant
  }
  
  // iPhone 13 - handle both space and hyphen
  if (name.includes('iphone 13') || name.includes('iphone-13') || identifier.includes('iphone14,')) {
    return 'iphone-13';
  }
  
  // iPhone SE - handle both space and hyphen
  if (name.includes('iphone se') || name.includes('iphone-se') || identifier.includes('iphone14,6')) {
    return 'iphone-se';
  }
  
  return 'standard';
};

export const detectMacModel = (
  deviceName?: string,
  formFactor?: 'laptop' | 'desktop' | 'mini' | 'display',
  modelIdentifier?: string
): string => {
  if (!deviceName && !modelIdentifier) return 'standard';
  
  const name = (deviceName || '').toLowerCase();
  const identifier = (modelIdentifier || '').toLowerCase();
  
  // MacBook Pro detection with year distinction
  if (name.includes('macbook pro') || name.includes('macbook-pro') || identifier.includes('macbookpro')) {
    // Check for older Touch Bar models (2020 and earlier)
    if (name.includes('2020') || 
        name.includes('2019') || 
        name.includes('2018') ||
        name.includes('touch bar') ||
        identifier.includes('macbookpro16') ||  // 2019-2020 models
        identifier.includes('macbookpro15')) {  // 2018-2019 models
      return 'macbook-pro-2020';
    }
    
    // Newer models (2021+) with M1 Pro/Max, M2 Pro/Max, M3 Pro/Max
    if (identifier.includes('macbookpro18') ||  // 2021 14"/16"
        identifier.includes('macbookpro19') ||  // 2023 14"/16"
        name.includes('2021') || 
        name.includes('2022') || 
        name.includes('2023') ||
        name.includes('2024') ||
        name.includes('m1 pro') ||
        name.includes('m1 max') ||
        name.includes('m2 pro') ||
        name.includes('m2 max') ||
        name.includes('m3 pro') ||
        name.includes('m3 max')) {
      return 'macbook-pro';
    }
    
    // Default to newer design for MacBook Pro
    return 'macbook-pro';
  }
  
  // MacBook Air - handle both "macbook air" and "macbook-air"
  if (name.includes('macbook air') || name.includes('macbook-air') || identifier.includes('macbookair')) {
    return 'macbook-air';
  }
  
  // iMac 2021 (24-inch)
  if (name.includes('imac') && (name.includes('2021') || name.includes('24'))) {
    return 'imac-2021';
  }
  
  // iMac 2020 (27-inch)
  if (name.includes('imac') && name.includes('2020')) {
    return 'imac-2020';
  }
  
  // iMac (generic)
  if (name.includes('imac') || identifier.includes('imac')) {
    return 'imac-2021'; // Default to newer model
  }
  
  // Mac Studio - handle both "mac studio" and "mac-studio"
  if (name.includes('mac studio') || name.includes('mac-studio') || identifier.includes('macstudio')) {
    return 'mac-studio';
  }
  
  // Mac Mini - handle both "mac mini" and "mac-mini"
  if (name.includes('mac mini') || name.includes('mac-mini') || identifier.includes('macmini')) {
    return 'mac-mini';
  }
  
  // Mac Pro - handle both "mac pro" and "mac-pro"
  if (name.includes('mac pro') || name.includes('mac-pro') || identifier.includes('macpro')) {
    return 'mac-pro';
  }
  
  // Studio Display - handle variations
  if (name.includes('studio display') || name.includes('studio-display')) {
    return 'studio-display';
  }
  
  // Pro Display XDR - handle variations
  if (name.includes('pro display') || name.includes('pro-display') || name.includes('xdr')) {
    return 'pro-display-xdr';
  }
  
  // Form factor based detection
  if (formFactor === 'laptop') return 'macbook-air';
  if (formFactor === 'desktop') return 'imac-2021';
  if (formFactor === 'mini') return 'mac-mini';
  if (formFactor === 'display') return 'studio-display';
  
  return 'standard';
};

export const detectAppleWatchModel = (
  deviceName?: string,
  modelIdentifier?: string
): string => {
  if (!deviceName && !modelIdentifier) return 'watch-7-8-9';
  
  const name = (deviceName || '').toLowerCase();
  const identifier = (modelIdentifier || '').toLowerCase();
  
  // Apple Watch Ultra
  if (name.includes('apple watch ultra') || identifier.includes('watch6,1') || identifier.includes('watch6,2')) {
    return 'watch-ultra';
  }
  
  // Apple Watch Nike
  if (name.includes('apple watch nike')) {
    return 'watch-nike';
  }
  
  // Apple Watch Series 9
  if (name.includes('apple watch series 9') || identifier.includes('watch6,14') || identifier.includes('watch6,18')) {
    return 'watch-7-8-9';
  }
  
  // Apple Watch Series 8
  if (name.includes('apple watch series 8') || identifier.includes('watch6,10') || identifier.includes('watch6,13')) {
    return 'watch-7-8-9';
  }
  
  // Apple Watch Series 7
  if (name.includes('apple watch series 7') || identifier.includes('watch6,6') || identifier.includes('watch6,9')) {
    return 'watch-7-8-9';
  }
  
  // Generic Apple Watch
  if (name.includes('apple watch')) {
    return 'watch-7-8-9';
  }
  
  // Special accessories/views
  if (name.includes('dock')) return 'watch-dock';
  if (name.includes('band')) return 'watch-band';
  if (name.includes('fitness')) return 'apple-fitness';
  
  return 'watch-7-8-9';
};

export const detectAppleTvModel = (
  deviceName?: string,
  modelIdentifier?: string
): string => {
  if (!deviceName && !modelIdentifier) return 'apple-tv-4k';
  
  const name = (deviceName || '').toLowerCase();
  const identifier = (modelIdentifier || '').toLowerCase();
  
  // Apple TV 4K
  if (name.includes('apple tv 4k') || identifier.includes('appletv11,') || identifier.includes('appletv14,')) {
    return 'apple-tv-4k';
  }
  
  // Apple TV HD
  if (name.includes('apple tv hd') || identifier.includes('appletv5,')) {
    return 'apple-tv-hd';
  }
  
  return 'apple-tv-4k';
};

export const detectHomePodModel = (
  deviceName?: string,
  modelIdentifier?: string
): string => {
  if (!deviceName && !modelIdentifier) return 'homepod-mini';
  
  const name = (deviceName || '').toLowerCase();
  const identifier = (modelIdentifier || '').toLowerCase();
  
  // HomePod mini
  if (name.includes('homepod mini') || identifier.includes('audiaccessory5,')) {
    return 'homepod-mini';
  }
  
  // HomePod (full size)
  if (name.includes('homepod') || identifier.includes('audiaccessory1,')) {
    return 'homepod';
  }
  
  return 'homepod-mini';
};

/**
 * Main device detection function
 * Returns the OS type and variant to use with AppleDeviceIcon
 */
export const detectAppleDevice = (
  osType: string,
  deviceName?: string,
  modelIdentifier?: string,
  formFactor?: 'laptop' | 'desktop' | 'mini' | 'display' | 'set-top-box' | 'speaker' | 'wearable' | 'headset'
): { osType: string; variant?: string } => {
  const os = osType.toLowerCase();
  
  switch (os) {
    case 'ios':
    case 'iphone':
      return {
        osType: 'ios',
        variant: detectIphoneModel(deviceName, modelIdentifier)
      };
      
    case 'macos':
    case 'mac':
    case 'darwin':
      return {
        osType: 'macos',
        variant: detectMacModel(deviceName, formFactor as any, modelIdentifier)
      };
      
    case 'ipados':
    case 'ipad':
      // Simple iPad detection - can be enhanced later
      if (deviceName?.toLowerCase().includes('pro')) return { osType: 'ipados', variant: 'pro' };
      if (deviceName?.toLowerCase().includes('air')) return { osType: 'ipados', variant: 'air' };
      return { osType: 'ipados', variant: 'standard' };
      
    case 'watchos':
    case 'watch':
      return {
        osType: 'watchos',
        variant: detectAppleWatchModel(deviceName, modelIdentifier)
      };
      
    case 'tvos':
    case 'appletv':
      return {
        osType: 'tvos',
        variant: detectAppleTvModel(deviceName, modelIdentifier)
      };
      
    case 'audioos':
    case 'homepod':
      return {
        osType: 'homepod',
        variant: detectHomePodModel(deviceName, modelIdentifier)
      };
      
    case 'visionos':
    case 'vision':
      return {
        osType: 'visionos',
        variant: 'vision-pro-outline'
      };
      
    default:
      return { osType };
  }
};
